import * as HttpClient from 'http-client';
import {ArgValidator} from '../Utils/ArgValidator';
import {Logger} from '../Utils/Logger';
import {StringUtils} from '../Utils/StringUtils';
import {UriHelper} from '../Utils/UriHelper';
import {Utils} from '../Utils/Utils';
import {ApiClientOptions} from './ApiClientOptions';
import {RequestBase} from './RequestBase';
import {ResponseBase} from './ResponseBase';
import {ErrorResponse} from './ErrorResponse';
import {ApiClientConsts} from './ApiClientConsts';
import {PromiseRetryOptions} from '../Utils/CommonTypes';
import {
    ApiClientErrorType, IRequestInfo, HandleErrorResponseType, ApiClientError,
    ClientRetryResponseEntry
} from './Types';
import {ApiClientErrorCodes, ApiClientErrorDefinitions} from './ApiClientErrorCodes';
import {PromiseUtils} from '../Utils/PromiseUtils';

interface IFetchResponse extends whatwgFetch.Response {
    textString: string;
}

/**
 * list of response entries from which one should match to do client retry
 */
const clientRetryResponseEntries: Array<ClientRetryResponseEntry> = [
    {httpStatusCode: null, errorCode: 100},
    {httpStatusCode: 500, errorCode: 2000},
    {httpStatusCode: 500, errorCode: 2003},
    {httpStatusCode: 503, errorCode: null}
];

const clientRetryOptions = {
    retryCount: 3,
    retryDelay: 5000 // in ms
};

type SendRequestResult<TResponse> = {
    response: TResponse;
    apiClientError: ApiClientError;
}

class SendRequestError<TResponse> extends Error {
    public name = 'SendRequestError';

    constructor(public sendResult: SendRequestResult<TResponse>) {
        super('');
    }
}

/**
 * @class ApiClient - base communication class responsible for sending requests to api server and receive responses.
 * Role of this class is to properly prepare correct url to api endpoint, setup security headers, serialize requests and responses,
 * and handle valid responses and/or response errors.
 */
export abstract class ApiClient {
    private _commonStack: HttpClient.Stack;

    private _keepAliveEnabled: boolean;
    private _keepAliveTime: number;
    private _keepAliveRefreshTime: number;
    private _keepAliveTimerHandle: number;
    private _keepAliveTimerStartTime: Date;
    private _keepAliveSendingRequest: boolean;
    private _lastSendRequestTime: Date;
    private _lastReceiveResponseTime: Date;
    private _sessionHasExpired: boolean;
    private _requestTimeout: number;

    private _keepAliveDeflection = 2; // deflection in seconds

    protected _apiUrl: string;
    protected _gameKey: string;
    protected _supportJwtToken: boolean;

    private static _bearerToken = ApiClientConsts.AuthenticationScheme + ' ' + ApiClientConsts.HeaderValues.token + '=';

    /**
     * @property 'onErrorResponse' - invoked when error response arrived from server.
     */
    public onErrorResponse: HandleErrorResponseType;

    /**
     *
     * @param gameKey - key for game module
     * @param options - options for api client
     */
    constructor(gameKey: string, options: ApiClientOptions) {
        this._gameKey = gameKey;
        this._sessionHasExpired = false;
        this.setOptions(options);
    }

    protected setOptions(options: ApiClientOptions): void {
        ArgValidator.ensureIsNotNullOrUndefined(options, 'options');
        ArgValidator.ensureStringIsNotNullOrEmpty(options.serverUrl, 'options.serverUrl');
        //ArgValidator.ensureStringIsNotNullOrEmpty(options.jwtToken, 'options.jwtToken');
        ArgValidator.ensureIsNotNullOrUndefined(options.keepAliveEnabled, 'options.keepAliveEnabled');

        if (options.keepAliveEnabled) {
            ArgValidator.ensureIsNotNullOrUndefined(options.keepAliveTime, 'options.keepAliveTime');
        }

        this._supportJwtToken = !StringUtils.isNullOrEmpty(options.jwtToken);

        this._apiUrl = URI(options.serverUrl).toString();
        this.initFetch(options.jwtToken);

        // keep alive section
        this._keepAliveEnabled = options.keepAliveEnabled;
        this._keepAliveTime = this._keepAliveEnabled ? options.keepAliveTime : 0;
        this._keepAliveRefreshTime = options.keepAliveRefreshTime;
        this._keepAliveSendingRequest = false;

        this._keepAliveTimerHandle = null;
        this._lastSendRequestTime = null;
        this._lastReceiveResponseTime = null;

        this._requestTimeout = options.serverRequestTimeout;

        if (this._keepAliveEnabled) {
            this.onKeepAlive = this.onKeepAlive.bind(this);
            this.startKeepAliveTimer();
        }
    }

    private initFetch(jwtToken: string): void {
        if (this._supportJwtToken) {
            ArgValidator.ensureStringIsNotNullOrEmpty(jwtToken, 'jwtToken');
        }

        if (this._supportJwtToken) {
            this._commonStack = HttpClient.createStack(
                HttpClient.auth(`Bearer ${jwtToken}`),
                HttpClient.accept(ApiClientConsts.ContentTypeJson),
                HttpClient.base(this._apiUrl),
                HttpClient.parseText('textString')
            );
        } else {
            this._commonStack = HttpClient.createStack(
                HttpClient.accept(ApiClientConsts.ContentTypeJson),
                HttpClient.base(this._apiUrl),
                HttpClient.parseText('textString')
            );
        }

        if (DEBUG) {
            if (this._supportJwtToken) {
                Logger.debugGroup(this.getLoggerGroup(), `init fetch auth header [${jwtToken}]`);
            } else {
                Logger.debugGroup(this.getLoggerGroup(), `init fetch`);
            }
        }
    }

    private startKeepAliveTimer(): void {
        this._keepAliveTimerStartTime = new Date();
        this._keepAliveTimerHandle = setInterval(this.onKeepAlive, this._keepAliveRefreshTime * 1000); // check each n-seconds

        if (DEBUG) {
            Logger.debugGroup(this.getLoggerGroup(), `started keep alive timer with keep alive every '${this._keepAliveTime}' seconds of inactivity with server.`);
        }
    }

    private stopKeepAliveTimer(): void {
        if (this._keepAliveTimerHandle) {
            clearInterval(this._keepAliveTimerHandle);
            this._keepAliveTimerHandle = null;
        }
    }

    private onKeepAlive(): void {
        var self = this;

        var loggerGroup = this.getLoggerGroup();

        if (this._keepAliveSendingRequest) {
            if (DEBUG) {
                Logger.debugGroup(loggerGroup, `event onKeepAlive, skipping becase 'keep alive request' is sending now...`);
            }
            return;
        }

        var timeStart = Utils.isNullOrUndefined(this._lastReceiveResponseTime)
            ? this._keepAliveTimerStartTime.getTime()
            : this._lastReceiveResponseTime.getTime();

        var timeEnd = Date.now();
        var elapsedSeconds = Math.round(((timeEnd - timeStart) / 1000) - this._keepAliveDeflection);

        if (elapsedSeconds >= this._keepAliveTime) {
            if (this._sessionHasExpired) {
                if (DEBUG) {
                    Logger.debugGroup(loggerGroup, `Detected ~${elapsedSeconds} seconds of inactivity with server, but session has expired already` +
                                                   ` so keep alive request will NOT be send to server! Stopping keep alive timer.`);
                }

                this.stopKeepAliveTimer();
            }
            else {
                if (DEBUG) {
                    Logger.debugGroup(loggerGroup, `Detected ~${elapsedSeconds} seconds of inactivity with server, sending keep alive request to server!`);
                }

                // send keep alive request to server
                this._keepAliveSendingRequest = true;
                this.sendKeepAliveRequest()
                    .then(() => {
                        if (DEBUG) {
                            Logger.debugGroup(loggerGroup, `keep alive request was send to server.`);
                        }
                    })
                    .catch(error => {
                        self._sessionHasExpired = true;
                    })
                    .finally(() => {
                        // start keep alive timer again
                        self._keepAliveSendingRequest = false;
                    });
            }
        } else {
            if (DEBUG) {
                Logger.debugGroup(loggerGroup, `event onKeepAlive, nothing to do now, client is inactive only` +
                                               ` for '${elapsedSeconds}' seconds (threeshold is '${this._keepAliveTime}' seconds)`);
            }
        }
    }

    private handleSecurity(response: IFetchResponse) {
        if (!this._supportJwtToken) {
            return;
        }

        var wwwAuth = response.headers.get(ApiClientConsts.HeaderNames.WWWAuthenticate) as string;
        if (wwwAuth && StringUtils.startsWith(wwwAuth, ApiClient._bearerToken)) {
            var jwtToken = wwwAuth.substr(ApiClient._bearerToken.length,
                wwwAuth.length - ApiClient._bearerToken.length).replace(/"/g, '');
            if (!StringUtils.isNullOrEmpty(jwtToken)) {
                if (DEBUG) {
                    Logger.debugGroup(this.getLoggerGroup(), `received jwt token in response header [${jwtToken}]`);
                }
                UriHelper.updateHashGroupValue('token', jwtToken);
                this.initFetch(jwtToken);
            }
        }
    }

    protected getLoggerGroup(): string {
        return `Game "${this._gameKey}"`;
    }

    protected get sessionHasExpired(): boolean {
        return this._sessionHasExpired;
    }

    protected handleInvalidResponse(response: string): any {
        if (DEBUG) {
            Logger.errorGroup(this.getLoggerGroup(), '[ApiClient] handleInvalidResponse for response: ' + response);
        }
        return null;
    }

    /**
     * @method handleErrorReponse - handle error response
     * @param clientError - client error
     */
    protected handleErrorReponse(clientError: ApiClientError): void {
        var errorStr = Utils.isNullOrUndefined(clientError.error) ? '-' : JSON.stringify(clientError.error);

        if (DEBUG) {
            Logger.debug(`[handleErrorReponse] errorType '${ApiClientErrorType[clientError.type]}', ` +
                         `httpStatusCode '${clientError.httpStatusCode}', error: ${errorStr}`);
        }

        if (clientError && clientError.hasDefinition(ApiClientErrorDefinitions.SessionHasExpired) ||
            clientError.type === ApiClientErrorType.Unauthorized) {
            this._sessionHasExpired = true;

            if (DEBUG) {
                Logger.debug(`[handleErrorReponse] sessionHasExpired was set to true!`);
            }
        }

        if (this.onErrorResponse) {
            this.onErrorResponse(clientError);
        }
    }

    /**
     * @method sendKeepAliveRequest - sends keep alive request to server to keep server session, ticket, etc alive
     * event when api client is inactive with server due some time (defined in @ApiClientOptions.keepAliveTime)
     */
    protected abstract sendKeepAliveRequest(): Promise<ResponseBase>;

    private fetchPatched<T>(fetch: any): Promise<T> {
        return Promise.resolve(fetch);
    }

    /**
     * sends request to server
     * @param request - request object as JSON to be send to api server
     * @param httpMethod - http method to use for request
     * @param routeUrl - route url where to send request
     * @param logHeader - header used to log messages
     * @param supressHandleError - flag if 'handleErrorResponse' will be supressed in case of error response or not,
     * default unspecified value means 'false', so error response is NOT supressed and its actually called.
     * @param overwriteRetryCount - overwrite retry count for this one send request call only
     * @typedef TResponse - Type for expected response json object.
     * @returns Promise<TResponse> - promise with response of type @Response or descendats of @Response type.
     */
    protected sendRequest<TResponse extends ResponseBase>(request: RequestBase<TResponse>,
        httpMethod: string, routeUrl: string, options?: ApiClientSendRequestOptions): Promise<TResponse> {
        var self = this;

        var logHeader = options ? options.logHeader : undefined;
        var supressHandleError = options ? options.supressHandleError : undefined;
        var overwriteRetryCount = options ? options.overwriteRetryCount : undefined;
        var customHandleError = options ? options.customHandleError : undefined;

        ArgValidator.ensureIsNotNullOrUndefined(request, 'request');

        supressHandleError = Utils.valueOrDefault(supressHandleError, false);
        var hasRetryCountOverwritten = !Utils.isNullOrUndefined(overwriteRetryCount);

        if (Utils.isNullOrUndefined(logHeader)) {
            logHeader = 'sending request to: ' + this._apiUrl + routeUrl + '...';
        }

        var loggerGroup = this.getLoggerGroup();

        if (DEBUG) {
            Logger.debugGroup(loggerGroup, logHeader);
        }

        var retryOptions: PromiseRetryOptions = {
            interval: clientRetryOptions.retryDelay,
            max_tries: clientRetryOptions.retryCount,
            predicate: (e: Error) => e instanceof SendRequestError
        };

        if (!Utils.isNullOrUndefined(this._requestTimeout)) {
            retryOptions.interval = this._requestTimeout;
        }

        if (hasRetryCountOverwritten) {
            retryOptions.max_tries = overwriteRetryCount;
        }

        var retryCount = 0;

        function invokeCustomHandleError(clientError: ApiClientError) {
            if (customHandleError) {
                try {
                    customHandleError(clientError);
                } catch (err) {
                    if (DEBUG) {
                        Logger.debugGroup('ApiClient', `Invoke customHandleError failed: ` + Utils.getErrorMsg(err));
                    }
                }
            }
        }

        function fnRetryProxy() {
            retryCount++;

            if (DEBUG) {
                if (retryCount > 1) {
                    Logger.debugGroup(loggerGroup, logHeader + ` retrying (#${retryCount}) send request...`);
                }
            }

            return self.internalSendRequest(request, httpMethod, routeUrl, logHeader)
                //.timeout(clientRetryOptions.retryDelay)
                .then(rsp => {
                    // if there is api client error...
                    if (!Utils.isNullOrUndefined(rsp.apiClientError)) {
                        var canRetry = ApiClient.canRetryOnApiClientError(rsp.apiClientError);

                        if (canRetry && retryCount < retryOptions.max_tries) {
                            // throw special error indicate for retryOptions.predicate to allow retry
                            throw new SendRequestError<TResponse>(rsp);
                        } else {
                            if (!supressHandleError) {
                                self.handleErrorReponse(rsp.apiClientError);
                            }
                            invokeCustomHandleError(rsp.apiClientError);
                        }
                    }

                    return rsp.response;
                })
                .catch(Promise.TimeoutError, () => {
                    if (DEBUG) {
                        Logger.debugGroup(loggerGroup, logHeader + ` request timeout after ${clientRetryOptions.retryDelay} ms`);
                    }

                    // when request call does not get response within specified time, retry operation
                    var requestInfo: IRequestInfo = {
                        request: request,
                        url: routeUrl,
                        gameKey: self._gameKey
                    };
                    var rsp: SendRequestResult<TResponse> = {
                        apiClientError: new ApiClientError(0, requestInfo, ApiClientErrorCodes.NetworkError),
                        response: null
                    };

                    if (retryCount < retryOptions.max_tries) {
                        // throw special error indicate for retryOptions.predicate to allow retry
                        throw new SendRequestError<TResponse>(rsp);
                    } else {
                        if (!supressHandleError) {
                            self.handleErrorReponse(rsp.apiClientError);
                        }
                        invokeCustomHandleError(rsp.apiClientError);
                    }
                });
        }

        return PromiseUtils.retry<TResponse>(() => {
                return fnRetryProxy();
            }, retryOptions)
            .catch(err => {
                if (err instanceof SendRequestError) {
                    return err.sendResult.response;
                }

                return null;
            });
    }

    private internalSendRequest<TResponse extends ResponseBase>(request: RequestBase<TResponse>,
        httpMethod: string, routeUrl: string, logHeader?: string): Promise<SendRequestResult<TResponse>> {

        var self = this;

        var loggerGroup = this.getLoggerGroup();

        var paramsOrBody: any;
        if (httpMethod.toUpperCase() === ApiClientConsts.HttpMethod.POST) {
            paramsOrBody = HttpClient.body(JSON.stringify(request), ApiClientConsts.ContentTypeJson);
        } else {
            paramsOrBody = HttpClient.params(request);
        }

        var fetch = HttpClient.createFetch(this._commonStack, HttpClient.method(httpMethod),
            paramsOrBody);

        this._lastSendRequestTime = new Date();

        var apiClientError: ApiClientError = null;

        var result = this.fetchPatched(fetch(routeUrl))
            .timeout(this._requestTimeout != null ? this._requestTimeout : clientRetryOptions.retryDelay)
            .then((response: IFetchResponse) => {
                if (DEBUG) {
                    Logger.debugGroup(loggerGroup, logHeader + ` received response [${response.status}]`);
                }

                self._lastReceiveResponseTime = new Date();

                var apiResponse: TResponse = null;
                var httpStatusCode = response.status;

                var textResponse: string = response.textString;
                var shouldHaveJsonBody = Utils.valueIsIn(httpStatusCode, [200, 400, 500]);
                var statusSuccess = httpStatusCode === 200;

                // check for updated jwt token and update fetch with it if available
                self.handleSecurity(response);

                var requestInfo: IRequestInfo = statusSuccess ? null : {
                    request: request,
                    url: response.url,
                    gameKey: self._gameKey
                } as IRequestInfo;

                if (shouldHaveJsonBody) {
                    var jsonParseResult = Utils.parseJson(textResponse, false);
                    if (!jsonParseResult.isValid) {
                        apiResponse = self.handleInvalidResponse(textResponse);
                    } else {
                        var responseJsonData = jsonParseResult.json;
                        var responseJson: Object;
                        switch (httpStatusCode) {
                            case 200: {
                                responseJson = responseJsonData;
                                apiResponse = request.createResponse(responseJson);

                                if (!apiResponse.validate()) {
                                    if (DEBUG) {
                                        Logger.errorGroup(loggerGroup, logHeader + ' received response is NOT valid');
                                        Logger.errorGroup(loggerGroup, logHeader + ' - errors: ', apiResponse.getValidateErrors());
                                    }
                                    apiResponse = null;
                                }

                                break;
                            }
                            case 400: // bad request
                            case 500: // internal server error
                            {
                                responseJson = responseJsonData;
                                var errorResponse = new ErrorResponse(responseJson);

                                if (!errorResponse.validate()) {
                                    if (DEBUG) {
                                        Logger.errorGroup(loggerGroup, logHeader + ' received error response is NOT valid');
                                        Logger.errorGroup(loggerGroup, logHeader + ' - errors: ', errorResponse.getValidateErrors());
                                    }
                                }

                                apiClientError = new ApiClientError(httpStatusCode, requestInfo, errorResponse);
                                break;
                            }
                        }
                    }
                } else {
                    apiClientError = new ApiClientError(httpStatusCode, requestInfo, null);
                }

                // NOTE(psulek) returns null even in error because we do not want from caller to use '.catch/.error' fns!
                return {
                    response: apiResponse,
                    apiClientError: apiClientError
                };
            })
            .catch(err => {
                if (DEBUG) {
                    Logger.errorGroup(loggerGroup, logHeader + ' failed', err);
                }

                var requestInfo: IRequestInfo = {
                    request: request,
                    url: routeUrl,
                    gameKey: self._gameKey
                };
                apiClientError = new ApiClientError(0, requestInfo, ApiClientErrorCodes.NetworkError);

                // NOTE(psulek) returns null even in error because we do not want from caller to use '.catch/.error' fns!
                //return null as TResponse;

                return {
                    response: null,
                    apiClientError: apiClientError
                };
            });

        return (result as any) as Promise<SendRequestResult<TResponse>>;
    }

    private static canRetryOnApiClientError(apiClientError: ApiClientError): boolean {
        var httpStatusCode = apiClientError.httpStatusCode;
        var errorCode = Utils.isNullOrUndefined(apiClientError.error) ? null : apiClientError.error.code;

        for (var i = 0; i < clientRetryResponseEntries.length; i++) {
            var entry = clientRetryResponseEntries[i];
            var match = Utils.isNullOrUndefined(entry.httpStatusCode) || entry.httpStatusCode === httpStatusCode;
            if (match) {
                match = Utils.isNullOrUndefined(entry.errorCode) || entry.errorCode === errorCode;
            }

            if (match) {
                return true;
            }
        }

        return false;
    }
}

export type ApiClientSendRequestOptions = {
    logHeader?: string;
    supressHandleError?: boolean;
    overwriteRetryCount?: number;
    customHandleError?: (clientError: ApiClientError) => void;
}



/** WEBPACK FOOTER **
 ** ./Engine/ApiClient/ApiClient.ts
 **/